export type PortalUser = {
  id: string
  clerk_user_id: string
  email?: string
  name?: string
}

export type PortalOrganization = {
  id: string
  clerk_organization_id?: string
  name: string
  slug: string
  image_url?: string
  role?: string
  is_admin?: boolean
}

export type PortalOrganizationMember = {
  id: string
  membership_id: string
  clerk_user_id: string
  email: string
  first_name?: string
  last_name?: string
  name: string
  image_url?: string
  role: string
  access_role: 'viewer' | 'editor' | 'admin'
  is_current: boolean
}

export type PortalOrganizationInvitation = {
  id: string
  email: string
  role: string
  status: string
  already_pending?: boolean
  created_at: number
}

export type PortalMemberOrganization = {
  id: string
  name: string
  slug: string
  image_url?: string
  role: string
}

export type PortalMemberProfile = {
  id: string
  clerk_user_id?: string
  email: string
  first_name?: string
  last_name?: string
  name: string
  image_url?: string
  is_current: boolean
  password_enabled?: boolean
  organizations: PortalMemberOrganization[]
}

export type PortalProject = {
  id: string
  organization_id: string
  team_id?: string
  name: string
  description?: string
  slug: string
  github_connection_id?: string
  effective_role?: 'viewer' | 'editor' | 'admin'
  capabilities?: string[]
}

export type PortalTeam = {
  id: string
  organization_id: string
  parent_team_id?: string
  name: string
  slug: string
  description?: string
  effective_role?: 'viewer' | 'editor' | 'admin'
}

export type PortalRoleBinding = {
  id: string
  organization_id: string
  user_id: string
  scope_kind: 'organization' | 'team' | 'project'
  scope_id: string
  role_family: 'access' | 'billing'
  role: 'viewer' | 'editor' | 'admin' | 'billing'
}

export type PortalGithubInstallation = {
  id: string
  organization_id: string
  installation_id: string
  account_id: string
  account_login: string
  account_type: string
  avatar_url?: string
}

export type PortalGithubConnection = {
  id: string
  organization_id: string
  github_installation_id: string
  installation_id: string
  repository_id: string
  owner: string
  repo: string
  default_branch?: string
  default_path?: string
  path_filter_enabled: boolean
}

export type PortalGithubRepository = {
  id: string
  name: string
  full_name: string
  owner: string
  private: boolean
  default_branch: string
  html_url: string
  updated_at: string
}

export type PortalGithubBranch = { name: string }

export type PortalEnvironment = {
  id: string
  project_id: string
  organization_id: string
  name: string
  slug: string
  branch: string
}

export type PortalEnvVar = {
  id: string
  environment_id: string
  name: string
  target: 'web' | 'tasks'
  task_queue?: string
  queue_status?: 'discovered' | 'pending' | 'orphaned' | string
  queue_run_id?: string
  sensitive: boolean
  scope: 'runtime' | 'build' | 'both'
  s3_key: string
  version: string
  /** Present only for non-sensitive values returned by the portal read model. */
  value?: string
}

// PortalDomainStatus mirrors custom_domains.status
// (docs/data-plane-contracts.md §4).
export type PortalDomainStatus =
  | 'pending_dns'
  | 'provisioning'
  | 'ready'
  | 'active'
  | 'failed'
  | 'expired'
  | 'deleting'

export type PortalDNSRecord = {
  type: string
  name: string
  value: string
  purpose: string
}

export type PortalDomain = {
  id: string
  environment_id: string
  kind: 'vanity' | 'external'
  hostname: string
  status: PortalDomainStatus
  ownership_status: string
  hostname_status: string
  certificate_status: string
  routing_dns_status: string
  required_dns_records: PortalDNSRecord[]
  last_checked_at?: string
  claim_expires_at?: string
  // Legacy challenge fields remain optional while CloudFront rollback rows
  // exist. New UI renders required_dns_records instead.
  ownership_txt_name?: string
  ownership_txt_value?: string
  ownership_verified_at?: string
  dns_validation_name?: string
  dns_validation_value?: string
  cf_target_dns?: string
  validated_at?: string
  failure_reason?: string
  created_at: string
  updated_at: string
}

export type PortalOrgDomain = {
  id: string
  organization_id: string
  team_id?: string
  domain: string
  status: 'pending' | 'verified'
  ownership_txt_name: string
  ownership_txt_value: string
  verified_at?: string
  last_checked_at?: string
  failure_reason?: string
  binding_count: number
  created_at: string
  updated_at: string
}

// PortalDeploymentStatus/PortalDeploymentTrigger mirror the authoritative
// DynamoDB deployment item.
export type PortalDeploymentStatus = 'in_progress' | 'succeeded' | 'failed' | 'superseded'
export type PortalDeploymentTrigger = 'manual' | 'rollback' | 'api' | 'push'

// PortalDeployment is one deployment-history item.
export type PortalDeployment = {
  id: string
  organization_id: string
  project_id: string
  environment_id: string
  run_id: string
  git_sha?: string
  commit_subject?: string
  status: PortalDeploymentStatus
  trigger: PortalDeploymentTrigger
  actor_user_id?: string
  source_deployment_id?: string
  source_snapshot_id?: string
  configuration_mode?: 'original' | 'draft'
  failure_reason?: string
  created_at: string
  updated_at: string
}

// PortalCurrentDeployment mirrors the DynamoDB CURRENT pointer.
export type PortalCurrentDeployment = {
  environment_id: string
  organization_id: string
  project_id: string
  run_id: string
  deployment_id: string
  updated_at: string
}

export type PortalDeploymentLifecycle = {
  queued_at?: string
  build_started_at?: string
  build_completed_at?: string
  build_status?: string
  build_phase?: string
  deployment_started_at?: string
  activated_at?: string
  serving_state?: string
  serving_at?: string
  completed_at?: string
}

export type PortalDeploymentDetail = {
  deployment: PortalDeployment
  current: boolean
  project_slug: string
  project_name: string
  environment_slug: string
  environment_name: string
  lifecycle: PortalDeploymentLifecycle
  timing_available: boolean
}

// PortalDeploymentActivity is one row of the org-wide activity feed: a
// deployment joined with its project/environment slug and name.
export type PortalDeploymentActivity = PortalDeployment & {
  project_slug: string
  project_name: string
  env_slug: string
  env_name: string
}

export type DeploymentRetentionPolicy = {
  scope_kind: 'organization' | 'team' | 'project'
  scope_id: string
  override_days?: number
  effective_days: number
  inherited_from: string
  inherited_scope?: string
}

export type AgentReviewRetentionMode = 'disabled' | 'transient' | 'retained'

export type AgentReviewRetentionOverride = {
  mode: AgentReviewRetentionMode
  retained_days?: number
}

export type AgentReviewRetentionPolicy = {
  scope_kind: 'organization' | 'team' | 'project'
  scope_id: string
  override?: AgentReviewRetentionOverride
  policy_version: number
  effective_mode: AgentReviewRetentionMode
  effective_retained_days: number
  effective_source: string
  effective_source_id?: string
  effective_version: string
  applies_to_new_data_only: boolean
}

export type PortalOrganizationAPIKey = {
  id: string
  name: string
  key_prefix: string
  fingerprint: string
  scopes?: string[]
  targets?: string[]
  expires_at: string
  revoked_at?: string
  last_used_at?: string
  created_at: string
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: {
      'content-type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
  })
  const text = await res.text()
  let body: unknown = null
  if (text) {
    try {
      body = JSON.parse(text)
    } catch {
      body = { error: text }
    }
  }
  if (!res.ok) {
    const err = body as { error?: string } | null
    throw new Error(err?.error || `Request failed (${res.status})`)
  }
  return body as T
}

export { api }

export type PortalNounPreviewRef = {
  kind: 'project' | 'environment' | 'team' | 'member' | 'deployment'
  slug?: string
  project_slug?: string
  env_slug?: string
  id?: string
  deployment_id?: string
}

export type PortalNounPreview = {
  key: string
  kind: PortalNounPreviewRef['kind']
  href: string
  title: string
  subtitle?: string
  status?: string
  description?: string
  metadata?: Record<string, unknown>
}

/** Fetches one authorized, deduplicated preview batch for hover/focus warming. */
export function getNounPreviews(refs: PortalNounPreviewRef[]) {
  return api<{ previews: Record<string, PortalNounPreview> }>('/api/noun-previews', {
    method: 'POST',
    body: JSON.stringify({ refs }),
  }).then((result) => Object.values(result.previews ?? {}).map((preview) => ({
    ...preview,
    status: preview.status ?? (typeof preview.metadata?.status === 'string' ? preview.metadata.status : undefined),
    metadata: Object.entries(preview.metadata ?? {}).map(([label, value]) => ({
      label: label.replaceAll('_', ' '),
      value: typeof value === 'string' ? value : JSON.stringify(value),
    })),
  })))
}

export function getUsage(params: {
  scope?: string
  granularity?: string
  project?: string
  deployment?: string
}) {
  const q = new URLSearchParams()
  if (params.scope) q.set('scope', params.scope)
  if (params.granularity) q.set('granularity', params.granularity)
  if (params.project) q.set('project', params.project)
  if (params.deployment) q.set('deployment', params.deployment)
  return api<{
    scope: string
    granularity: string
    series: Array<Record<string, unknown>>
    note?: string
    watermark?: string
    backend?: string
  }>(`/api/usage?${q}`)
}

export function listTraces(params: {
  project?: string
  environment?: string
  deployment?: string
  request?: string
  cursor?: string
}) {
  const q = new URLSearchParams()
  if (params.project) q.set('project', params.project)
  if (params.environment) q.set('environment', params.environment)
  if (params.deployment) q.set('deployment', params.deployment)
  if (params.request) q.set('request', params.request)
  if (params.cursor) q.set('cursor', params.cursor)
  return api<{
    traces: Array<Record<string, unknown>>
    access_hits: Array<Record<string, unknown>>
    next_cursor?: string
    note?: string
    watermark?: string
    backend?: string
  }>(`/api/traces?${q}`)
}

export function bootstrapSession(input: {
  email?: string
  name?: string
  organization_name?: string
}) {
  return api<{
    user: PortalUser
    organization?: PortalOrganization
    membership?: { role: string }
  }>('/api/session/bootstrap', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function getOrganization() {
  return api<PortalOrganization>('/api/organization')
}

export function updateOrganization(input: { name: string; slug: string }) {
  return api<PortalOrganization>('/api/organization', {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

export function getOrganizationRetention() {
  return api<DeploymentRetentionPolicy>('/api/organization?action=retention')
}

export function setOrganizationRetention(overrideDays: number | null) {
  return api<DeploymentRetentionPolicy>('/api/organization?action=retention', {
    method: 'PATCH',
    body: JSON.stringify({ override_days: overrideDays }),
  })
}

export function getOrganizationAgentReviewRetention() {
  return api<AgentReviewRetentionPolicy>('/api/organization?action=agent-review-retention')
}

export function setOrganizationAgentReviewRetention(override: AgentReviewRetentionOverride | null) {
  return api<AgentReviewRetentionPolicy>('/api/organization?action=agent-review-retention', {
    method: 'PATCH',
    body: JSON.stringify({ agent_review_override: override }),
  })
}

export function recalculateOrganizationRetention() {
  return api<{ status: string; workflow_id: string }>(
    '/api/organization?action=recalculate-retention',
    { method: 'PATCH', body: '{}' },
  )
}

export function syncOrganization() {
  return api<{ organization: PortalOrganization }>('/api/organization?action=sync', {
    method: 'POST',
    body: '{}',
  })
}

export function listOrganizationMembers(query = '', offset = 0) {
  const params = new URLSearchParams({
    action: 'members',
    query,
    offset: String(offset),
    limit: '50',
  })
  return api<{ members: PortalOrganizationMember[]; total: number }>(
    `/api/organization?${params.toString()}`,
  )
}

export function updateOrganizationMember(userId: string, accessRole: 'viewer' | 'editor' | 'admin') {
  return api<{ role: string }>('/api/organization?action=member', {
    method: 'PATCH',
    body: JSON.stringify({ user_id: userId, access_role: accessRole }),
  })
}

export function removeOrganizationMember(userId: string) {
  return api<{ removed: boolean }>('/api/organization?action=member', {
    method: 'DELETE',
    body: JSON.stringify({ user_id: userId }),
  })
}

export function listOrganizationInvitations() {
  return api<{ invitations: PortalOrganizationInvitation[]; total: number }>(
    '/api/organization?action=invitations',
  )
}

export function inviteOrganizationMember(
  email: string,
  role: 'org:admin' | 'org:member',
  access?: {
    scope_kind?: 'organization' | 'team' | 'project'
    scope_id?: string
    access_role: 'viewer' | 'editor' | 'admin'
  },
) {
  return api<PortalOrganizationInvitation>('/api/organization?action=invitation', {
    method: 'POST',
    body: JSON.stringify({ email, role, ...(access ?? {}) }),
  })
}

export function revokeOrganizationInvitation(invitationId: string) {
  return api<{ revoked: boolean }>('/api/organization?action=invitation', {
    method: 'DELETE',
    body: JSON.stringify({ invitation_id: invitationId }),
  })
}

export function leaveOrganization() {
  return api<{ left: boolean }>('/api/organization?action=leave', {
    method: 'POST',
    body: '{}',
  })
}

export function deleteOrganization(confirmation: string) {
  return api<{ deleted: boolean }>('/api/organization', {
    method: 'DELETE',
    body: JSON.stringify({ confirmation }),
  })
}

export function listOrganizationAPIKeys() {
  return api<{ api_keys: PortalOrganizationAPIKey[] }>('/api/org/api-keys')
}

export function createOrganizationAPIKey(input: { name: string; scopes: string[]; targets?: string[]; expires_at?: string }) {
  return api<{ api_key: PortalOrganizationAPIKey; secret: string }>('/api/org/api-keys', {
    method: 'POST', body: JSON.stringify(input),
  })
}

export function revokeOrganizationAPIKey(id: string) {
  return api<{ revoked: boolean }>('/api/org/api-keys/revoke', { method: 'POST', body: JSON.stringify({ id }) })
}

export function deleteOrganizationAPIKey(id: string) {
  return api<{ deleted: boolean }>('/api/org/api-keys', { method: 'DELETE', body: JSON.stringify({ id }) })
}

export function getMemberProfile(id: string) {
  return api<PortalMemberProfile>(`/api/members?id=${encodeURIComponent(id)}`)
}

export function syncMemberProfile() {
  return api<PortalMemberProfile>('/api/members?action=sync', {
    method: 'POST',
    body: '{}',
  })
}

export function listProjects() {
  return api<{ projects: PortalProject[] }>('/api/projects')
}

export function createProject(input: {
  name: string
  description?: string
  team_id?: string
}) {
  return api<PortalProject>('/api/projects', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}
export function updateProject(input: { slug: string; name: string; description?: string }) {
  return api<PortalProject>('/api/projects', { method: 'PATCH', body: JSON.stringify(input) })
}

export function getProjectRetention(slug: string) {
  return api<DeploymentRetentionPolicy>(
    `/api/projects/get?action=retention&slug=${encodeURIComponent(slug)}`,
  )
}

export function setProjectRetention(slug: string, overrideDays: number | null) {
  return api<DeploymentRetentionPolicy>('/api/projects?action=retention', {
    method: 'PATCH',
    body: JSON.stringify({ slug, override_days: overrideDays }),
  })
}

export function getProjectAgentReviewRetention(slug: string) {
  return api<AgentReviewRetentionPolicy>(
    `/api/projects/get?action=agent-review-retention&slug=${encodeURIComponent(slug)}`,
  )
}

export function setProjectAgentReviewRetention(slug: string, override: AgentReviewRetentionOverride | null) {
  return api<AgentReviewRetentionPolicy>('/api/projects?action=agent-review-retention', {
    method: 'PATCH',
    body: JSON.stringify({ slug, agent_review_override: override }),
  })
}

export function recalculateProjectRetention(slug: string) {
  return api<{ status: string; workflow_id: string }>(
    '/api/projects?action=recalculate-retention',
    { method: 'PATCH', body: JSON.stringify({ slug }) },
  )
}

export function listTeams() {
  return api<{ teams: PortalTeam[] }>('/api/teams')
}

export function createTeam(input: {
  name: string
  description?: string
  parent_team_id?: string
}) {
  return api<PortalTeam>('/api/teams', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function updateTeamName(input: { id: string; name: string }) {
  return api<PortalTeam>('/api/teams', {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

export function getTeamRetention(id: string) {
  return api<DeploymentRetentionPolicy>(
    `/api/teams?action=retention&id=${encodeURIComponent(id)}`,
  )
}

export function setTeamRetention(id: string, overrideDays: number | null) {
  return api<DeploymentRetentionPolicy>('/api/teams?action=retention', {
    method: 'PATCH',
    body: JSON.stringify({ id, override_days: overrideDays }),
  })
}

export function getTeamAgentReviewRetention(id: string) {
  return api<AgentReviewRetentionPolicy>(
    `/api/teams?action=agent-review-retention&id=${encodeURIComponent(id)}`,
  )
}

export function setTeamAgentReviewRetention(id: string, override: AgentReviewRetentionOverride | null) {
  return api<AgentReviewRetentionPolicy>('/api/teams?action=agent-review-retention', {
    method: 'PATCH',
    body: JSON.stringify({ id, agent_review_override: override }),
  })
}

export function recalculateTeamRetention(id: string) {
  return api<{ status: string; workflow_id: string }>(
    '/api/teams?action=recalculate-retention',
    { method: 'PATCH', body: JSON.stringify({ id }) },
  )
}

export function listRoleBindings(scopeKind: PortalRoleBinding['scope_kind'], scopeId: string) {
  return api<{ role_bindings: PortalRoleBinding[] }>(
    `/api/role-bindings?scope_kind=${encodeURIComponent(scopeKind)}&scope_id=${encodeURIComponent(scopeId)}`,
  )
}

export function upsertRoleBinding(input: {
  user_id: string
  scope_kind: PortalRoleBinding['scope_kind']
  scope_id: string
  role_family: PortalRoleBinding['role_family']
  role: PortalRoleBinding['role']
}) {
  return api<PortalRoleBinding>('/api/role-bindings', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function revokeRoleBinding(id: string) {
  return api<{ revoked: boolean }>('/api/role-bindings', {
    method: 'DELETE',
    body: JSON.stringify({ id }),
  })
}

export function listGithubInstallations(projectSlug?: string) {
  const query = projectSlug ? `?project_slug=${encodeURIComponent(projectSlug)}` : ''
  return api<{ installations: PortalGithubInstallation[] }>(`/api/github${query}`)
}

export function listGithubRepositories(githubInstallationId: string) {
  return api<{ repositories: PortalGithubRepository[] }>(
    `/api/github?action=repositories&github_installation_id=${encodeURIComponent(githubInstallationId)}`,
  )
}

export function listGithubBranches(projectSlug: string) {
  return api<{ branches: PortalGithubBranch[] }>(
    `/api/github?action=branches&project_slug=${encodeURIComponent(projectSlug)}`,
    { cache: 'no-store' },
  )
}

export function getProjectGithubConnection(projectSlug: string) {
  return api<{ connection: PortalGithubConnection | null }>(
    `/api/github?action=project&project_slug=${encodeURIComponent(projectSlug)}`,
  )
}

export function linkProjectGithubRepository(input: {
  project_slug: string
  github_installation_id: string
  repository_id: string
  default_path?: string
  path_filter_enabled: boolean
}) {
  return api<PortalGithubConnection>('/api/github', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function disconnectProjectGithubRepository(projectSlug: string) {
  return api<{ disconnected: boolean }>(
    `/api/github?project_slug=${encodeURIComponent(projectSlug)}`,
    { method: 'DELETE' },
  )
}

export function getProject(slug: string) {
  return api<PortalProject>(`/api/projects/get?slug=${encodeURIComponent(slug)}`)
}

export function listEnvironments(projectSlug: string) {
  return api<{ environments: PortalEnvironment[] }>(
    `/api/environments?project_slug=${encodeURIComponent(projectSlug)}`,
  )
}

export function createEnvironment(input: {
  project_slug: string
  name: string
  branch: string
}) {
  return api<PortalEnvironment>('/api/environments', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function updateEnvironment(input: { project_slug: string; slug: string; name: string; branch: string }) {
  return api<PortalEnvironment>('/api/environments', {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

export function deleteEnvironment(projectSlug: string, slug: string) {
  return api<{ deleted: boolean; slug: string }>('/api/environments', {
    method: 'DELETE',
    body: JSON.stringify({ project_slug: projectSlug, slug }),
  })
}

/** Production and preview targets cannot be removed from the portal. */
export function isProtectedEnvironmentSlug(slug: string): boolean {
  const normalized = slug.trim().toLowerCase()
  return normalized === 'production' || normalized === 'preview'
}

export function listVariables(projectSlug: string, envSlug: string) {
  return api<{ variables: PortalEnvVar[] }>(
    `/api/variables?project_slug=${encodeURIComponent(projectSlug)}&env_slug=${encodeURIComponent(envSlug)}`,
  )
}

export function upsertVariable(input: {
  project_slug: string
  env_slug: string
  name: string
  target?: 'web' | 'tasks' | undefined
  task_queue?: string | undefined
  sensitive: boolean
  scope: 'runtime' | 'build' | 'both'
  value?: string
}) {
  return api<PortalEnvVar>('/api/variables', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function deleteVariable(
  projectSlug: string,
  envSlug: string,
  name: string,
  target?: 'web' | 'tasks',
  taskQueue?: string,
) {
  return api<{ deleted: boolean; name: string }>('/api/variables', {
    method: 'DELETE',
    body: JSON.stringify({
      project_slug: projectSlug,
      env_slug: envSlug,
      name,
      target,
      task_queue: taskQueue,
    }),
  })
}

export type PortalTaskQueue = {
  queue: string
  status: 'discovered' | 'pending' | 'orphaned' | string
  run_id?: string
}

export function listTaskQueues(projectSlug: string, envSlug: string) {
  return api<{ task_queues: PortalTaskQueue[] }>(
    `/api/workflows/queues?project_slug=${encodeURIComponent(projectSlug)}&env_slug=${encodeURIComponent(envSlug)}`,
  )
}

export function rotateEnvironmentKey(projectSlug: string, envSlug: string) {
  const query = new URLSearchParams({
    action: 'rotate-key',
    project_slug: projectSlug,
    env_slug: envSlug,
  })
  return api<{
    environment_id: string
    key_version: number
    values_rotated: number
  }>(`/api/variables?${query.toString()}`, {
    method: 'POST',
    body: '{}',
  })
}

export function listDomains(projectSlug: string, envSlug: string) {
  return api<{ domains: PortalDomain[]; can_edit_domains: boolean; can_authorize_domains: boolean }>(
    `/api/domains?project_slug=${encodeURIComponent(projectSlug)}&env_slug=${encodeURIComponent(envSlug)}`,
  )
}

export function inspectDomain(hostname: string) {
  return api<{ hostname: string; is_apex: boolean }>(
    `/api/domains?inspect_hostname=${encodeURIComponent(hostname)}`,
  )
}

export function createDomain(input: {
  project_slug: string
  env_slug: string
  kind: 'vanity' | 'external'
  label?: string
  hostname?: string
  include_www?: boolean
}) {
  return api<{ status: string; workflow_id: string; domain: string; binding_id?: string }>('/api/domains', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function verifyDomain(input: {
  project_slug: string
  env_slug: string
  binding_id: string
}) {
  return api<{ status: string; workflow_id: string; domain: string }>('/api/domains/verify', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function removeDomain(input: {
  project_slug: string
  env_slug: string
  binding_id: string
}) {
  return api<{ status: string; workflow_id: string; domain: string }>('/api/domains/remove', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function listOrgDomains() {
  return api<{ domains: PortalOrgDomain[] }>('/api/org/domains')
}

export function authorizeOrgDomain(domain: string, teamId?: string) {
  return api<PortalOrgDomain>('/api/org/domains', {
    method: 'POST',
    body: JSON.stringify({ domain, ...(teamId ? { team_id: teamId } : {}) }),
  })
}

export function verifyOrgDomain(domain: string) {
  return api<PortalOrgDomain>('/api/org/domains/verify', {
    method: 'POST',
    body: JSON.stringify({ domain }),
  })
}

export function removeOrgDomain(domain: string) {
  return api<{ removed: boolean }>('/api/org/domains', {
    method: 'DELETE',
    body: JSON.stringify({ domain }),
  })
}

// listDeploymentActivity fetches the org-wide recent deploy activity feed
// (top-level Deployments view).
export function listDeploymentActivity(limit?: number) {
  const query = limit ? `?limit=${encodeURIComponent(String(limit))}` : ''
  return api<{ deployments: PortalDeploymentActivity[] }>(`/api/deployments${query}`)
}

export function listTeamDeploymentActivity(teamId: string, limit?: number) {
  const query = new URLSearchParams({ team_id: teamId })
  if (limit) query.set('limit', String(limit))
  return api<{ deployments: PortalDeploymentActivity[] }>(`/api/deployments?${query.toString()}`)
}

// listEnvironmentDeployments fetches deploy history plus the CURRENT
// pointer for one environment. `current` is null when the environment has
// never been deployed.
export function listEnvironmentDeployments(projectSlug: string, envSlug: string, cursor?: string) {
  const query = new URLSearchParams({ project_slug: projectSlug, env_slug: envSlug })
  if (cursor) query.set('cursor', cursor)
  return api<{ deployments: PortalDeployment[]; current: PortalCurrentDeployment | null; next_cursor?: string }>(
    `/api/deployments/environment?${query.toString()}`,
  )
}

export function getDeploymentDetail(projectSlug: string, envSlug: string, deploymentID: string) {
  const query = new URLSearchParams({ project_slug: projectSlug, env_slug: envSlug, deployment_id: deploymentID })
  return api<PortalDeploymentDetail>(`/api/deployments/detail?${query}`)
}

// triggerDeploy starts (or attaches to) a DeployEnvironment workflow.
// A rollback names the immutable source deployment. `original` restores its
// deploy-time configuration snapshot; `draft` combines its code with current
// runtime-only draft values.
export function triggerDeploy(input: {
  project_slug: string
  env_slug: string
  run_id?: string
  rollback_deployment_id?: string
  config_mode?: 'original' | 'draft'
}) {
  return api<{ status: string; workflow_id: string; run_id: string }>('/api/deploys', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export type PortalAccessMatch = {
  paths: string[]
  methods: string[]
}

export type PortalAccessAction = {
  type: 'allow' | 'deny' | 'maintenance'
  status?: number
}

export type PortalAccessRule = {
  id: string
  rule_key: string
  priority: number
  enabled: boolean
  match: PortalAccessMatch
  action: PortalAccessAction
  created_at?: string
  updated_at?: string
}

export type PortalAccessPolicy = {
  id: string
  organization_id: string
  project_id: string
  environment_id: string
  enabled: boolean
  active_version?: string
  updated_at: string
}

export function listAccessRules(projectSlug: string, envSlug: string) {
  return api<{
    policy: PortalAccessPolicy
    rules: PortalAccessRule[]
    effective?: { version: string; rules: unknown[] }
  }>(
    `/api/access-rules?project_slug=${encodeURIComponent(projectSlug)}&env_slug=${encodeURIComponent(envSlug)}`,
  )
}

export function upsertAccessRule(input: {
  project_slug: string
  env_slug: string
  id?: string
  rule_key: string
  priority: number
  enabled: boolean
  match: PortalAccessMatch
  action: PortalAccessAction
}) {
  return api<{ rule: PortalAccessRule }>('/api/access-rules', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function removeAccessRule(input: { project_slug: string; env_slug: string; id: string }) {
  return api<{ ok: boolean }>('/api/access-rules/remove', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function publishAccessRules(input: { project_slug: string; env_slug: string }) {
  return api<{ version: { version: string; status: string; checksum: string } }>(
    '/api/access-rules/publish',
    { method: 'POST', body: JSON.stringify(input) },
  )
}

export function emergencyDisableAccessRules(input: { project_slug: string; env_slug: string }) {
  return api<{ ok: boolean; enabled: boolean }>('/api/access-rules/emergency-disable', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function enableAccessRules(input: { project_slug: string; env_slug: string }) {
  return api<{ ok: boolean; enabled: boolean }>('/api/access-rules/enable', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export type PortalWorkflowRun = {
  organization_id: string
  project_id: string
  environment_id: string
  workflow_id: string
  run_id: string
  workflow_name: string
  task_queue: string
  namespace?: string
  status: string
  /** RFC3339 from portal JSON; SoR Dynamo stores unix ms (dual-read seconds). */
  started_at: string
  /** Last-touch only — not run end time; use closed_at for terminal end. */
  updated_at: string
  closed_at?: string
  cache_invalidation?: {
    hosts?: string[]
    tags?: string[]
    paths?: string[]
    deployment_ids?: string[]
    cache_generation?: string
    purge_status?: string
    purge_error?: string
    attempts?: number
    error?: string
  }
}

export type PortalWorkflowSchedule = {
  schedule_id: string
  workflow_name: string
  schedule_expression: string
  status: string
  timezone?: string
  updated_at?: string
}

export type PortalAgentExecution = {
  agent_id?: string
  conversation_id: string
  execution_id?: string
  workflow_id: string
  run_id: string
  workflow_name: string
  task_queue: string
  namespace?: string
  execution_state: string
  durable: boolean
  review_available: boolean
  review_handle?: string
  review_state: 'retained' | 'gap' | 'expired' | 'unavailable'
  review_expires_at?: string
  started_at: string
  updated_at: string
  closed_at?: string
}

export function listAgentExecutions(params: {
  projectSlug: string
  envSlug: string
  status?: string
  taskQueue?: string
  cursor?: string
  limit?: number
}) {
  const q = new URLSearchParams({ project_slug: params.projectSlug, env_slug: params.envSlug })
  if (params.status) q.set('status', params.status)
  if (params.taskQueue) q.set('task_queue', params.taskQueue)
  if (params.cursor) q.set('cursor', params.cursor)
  if (params.limit) q.set('limit', String(params.limit))
  return api<{
    executions: PortalAgentExecution[]
    next_cursor?: string
    environment_id: string
    source: 'durable_workflows'
  }>(`/api/agents/executions?${q}`)
}

export type PortalAgentTranscript = {
  events: unknown[]
  timeline: PortalAgentTranscriptItem[]
  next_cursor?: string
  state: 'retained' | 'gap' | 'expired' | 'unavailable'
  review_handle?: string
}

export type PortalAgentTranscriptItem = {
  kind: 'event' | 'gap'
  accepted_at: string
  event?: unknown
  gap?: {
    event_id: string
    stage: string
    recorded_at: string
  }
}

export function getAgentTranscript(input: {
  projectSlug: string
  envSlug: string
  workflowId: string
  runId: string
  cursor?: string
  limit?: number
}) {
  const q = new URLSearchParams({
    project_slug: input.projectSlug,
    env_slug: input.envSlug,
    workflow_id: input.workflowId,
    run_id: input.runId,
  })
  if (input.cursor) q.set('cursor', input.cursor)
  if (input.limit) q.set('limit', String(input.limit))
  return api<PortalAgentTranscript>(`/api/agents/transcript?${q}`)
}

export type PortalAgentLiveStreamGrant = {
  state: 'available' | 'unavailable'
  stream_url?: string
  grant?: string
  expires_in_seconds?: number
}

export type PortalAgentLiveStreamEvent = {
  cursor: string
  event_id: string
  type: string
  occurred_at: number
  accepted_at: string
  payload: unknown
}

export function createAgentLiveStreamGrant(input: {
  projectSlug: string
  envSlug: string
  workflowId: string
  runId: string
}) {
  const q = new URLSearchParams({
    project_slug: input.projectSlug,
    env_slug: input.envSlug,
    workflow_id: input.workflowId,
    run_id: input.runId,
  })
  return api<PortalAgentLiveStreamGrant>(`/api/agents/stream-grant?${q}`, {
    method: 'POST',
  })
}

export class AgentLiveStreamGapError extends Error {
  earliestCursor: string | undefined

  constructor(earliestCursor?: string) {
    super('Some live conversation events have expired.')
    this.name = 'AgentLiveStreamGapError'
    this.earliestCursor = earliestCursor
  }
}

export async function queryAgentLiveStream(input: {
  grant: PortalAgentLiveStreamGrant
  cursor?: string
  count?: number
  blockMs?: number
  signal?: AbortSignal
}): Promise<{ events: PortalAgentLiveStreamEvent[]; next_cursor?: string }> {
  if (
    input.grant.state !== 'available' ||
    !input.grant.stream_url ||
    !input.grant.grant
  ) {
    throw new Error('Agent live streaming is unavailable.')
  }
  const sameOriginBase = 'https://app.invalid'
  const target = new URL(input.grant.stream_url, sameOriginBase)
  if (target.origin !== sameOriginBase) {
    throw new Error('Agent live stream URL must be same-origin.')
  }
  if (input.cursor) target.searchParams.set('cursor', input.cursor)
  target.searchParams.set(
    'count',
    String(Math.max(1, Math.min(input.count ?? 128, 128))),
  )
  target.searchParams.set(
    'block_ms',
    String(Math.max(0, Math.min(input.blockMs ?? 15_000, 15_000))),
  )
  const response = await fetch(`${target.pathname}${target.search}`, {
    method: 'GET',
    cache: 'no-store',
    credentials: 'omit',
    headers: {
      accept: 'text/event-stream',
      authorization: `Bearer ${input.grant.grant}`,
    },
    ...(input.signal ? { signal: input.signal } : {}),
  })
  if (response.status === 410) {
    const gap = (await response.json().catch(() => null)) as {
      earliest_cursor?: string
    } | null
    throw new AgentLiveStreamGapError(gap?.earliest_cursor)
  }
  if (!response.ok) {
    throw new Error(`Agent live stream request failed (${response.status}).`)
  }
  const events = parseAgentLiveStreamEvents(await response.text())
  const nextCursor = events.at(-1)?.cursor ?? input.cursor
  return nextCursor ? { events, next_cursor: nextCursor } : { events }
}

export function parseAgentLiveStreamEvents(
  body: string,
): PortalAgentLiveStreamEvent[] {
  const events: PortalAgentLiveStreamEvent[] = []
  for (const block of body.split(/\r?\n\r?\n/)) {
    const lines = block.split(/\r?\n/)
    if (!lines.some((line) => line.trim() === 'event: agent-review')) continue
    const data = lines
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).replace(/^ /, ''))
      .join('\n')
    if (!data) continue
    let candidate: unknown
    try {
      candidate = JSON.parse(data)
    } catch {
      continue
    }
    if (
      typeof candidate !== 'object' ||
      candidate === null ||
      typeof (candidate as { cursor?: unknown }).cursor !== 'string' ||
      typeof (candidate as { event_id?: unknown }).event_id !== 'string' ||
      typeof (candidate as { type?: unknown }).type !== 'string'
    ) {
      continue
    }
    events.push(candidate as PortalAgentLiveStreamEvent)
  }
  return events
}

export function listWorkflowRuns(params: {
  projectSlug: string
  envSlug: string
  status?: string
  workflowName?: string
  taskQueue?: string
  cursor?: string
  limit?: number
}) {
  const q = new URLSearchParams({ project_slug: params.projectSlug, env_slug: params.envSlug })
  if (params.status) q.set('status', params.status)
  if (params.workflowName) q.set('workflow_name', params.workflowName)
  if (params.taskQueue) q.set('task_queue', params.taskQueue)
  if (params.cursor) q.set('cursor', params.cursor)
  if (params.limit) q.set('limit', String(params.limit))
  return api<{ runs: PortalWorkflowRun[]; next_cursor?: string; environment_id: string }>(`/api/workflows/runs?${q}`)
}

export function getWorkflowRun(projectSlug: string, envSlug: string, workflowId: string, runId: string) {
  const q = new URLSearchParams({
    project_slug: projectSlug,
    env_slug: envSlug,
    workflow_id: workflowId,
    run_id: runId,
  })
  return api<{
    run: PortalWorkflowRun
    events: Array<Record<string, unknown>>
    activities: Array<Record<string, unknown>>
    claim_ref_count: number
  }>(`/api/workflows/run?${q}`)
}

export function decryptWorkflowClaim(input: {
  project_slug: string
  env_slug: string
  workflow_id: string
  run_id: string
  claim_index?: number
}) {
  return api<{ plaintext: unknown; unavailable?: string }>('/api/workflows/decrypt', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function workflowRunAction(input: {
  project_slug: string
  env_slug: string
  action: 'signal' | 'cancel' | 'terminate' | 'retry'
  workflow_id: string
  run_id?: string
  signal_name?: string
  reason?: string
  worker_id?: string
  workflow_name?: string
  args?: unknown
}) {
  return api<{ ok: boolean; action: string; start?: { workflow_id: string; run_id: string } }>('/api/workflows/action', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function listWorkflowQueues(projectSlug: string, envSlug: string) {
  const q = new URLSearchParams({ project_slug: projectSlug, env_slug: envSlug })
  return api<{
    open_work: Array<{ task_queue: string; status: string; count: number }>
    worker_tips: Array<Record<string, unknown>>
    note?: string
  }>(`/api/workflows/queues?${q}`)
}

export function listWorkflowSchedules(projectSlug: string, envSlug: string) {
  const q = new URLSearchParams({ project_slug: projectSlug, env_slug: envSlug })
  return api<{ schedules: PortalWorkflowSchedule[] }>(`/api/workflows/schedules?${q}`)
}

export function createWorkflowSchedule(input: {
  project_slug: string
  env_slug: string
  schedule_id: string
  workflow_name: string
  schedule_expression: string
  worker_id?: string
  timezone?: string
  args?: unknown
}) {
  return api<{ schedule_id: string; status: string }>('/api/workflows/schedules', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function cancelWorkflowSchedule(input: {
  project_slug: string
  env_slug: string
  schedule_id: string
}) {
  return api<{ ok: boolean }>('/api/workflows/schedules/cancel', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}
